import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import { AccessLevel, PrivilegeHolderType } from "@core/api";
import { AccessTableConstants } from "./AccessTableConstants.js";
var sortByCallerPartOfAndByName = function(a, b) {
    var ref, ref1;
    var featuredWeight = 0;
    if (a.privilegeHolderType === PrivilegeHolderType.GROUP && b.privilegeHolderType === PrivilegeHolderType.GROUP) {
        if (a.callerPartOf > b.callerPartOf) {
            featuredWeight = -10;
        }
        if (a.callerPartOf < b.callerPartOf) {
            featuredWeight = 10;
        }
    }
    var displayNameA = ((ref = a.displayName) === null || ref === void 0 ? void 0 : ref.toLowerCase()) || "";
    var displayNameB = ((ref1 = b.displayName) === null || ref1 === void 0 ? void 0 : ref1.toLowerCase()) || "";
    if (displayNameA < displayNameB) {
        return -1 + featuredWeight;
    }
    if (displayNameA > displayNameB) {
        return 1 + featuredWeight;
    }
    return featuredWeight;
};
var isModifiableAccess = function(accessModel) {
    return [
        AccessLevel.VIEW_ALL,
        AccessLevel.CUSTOM
    ].includes(accessModel.accessLevel);
};
var sortModels = function(accessModels) {
    var featuredAccessModel = accessModels.filter(isModifiableAccess).sort(sortByCallerPartOfAndByName);
    var restAccessModel = accessModels.filter(function(accessModel) {
        return !isModifiableAccess(accessModel);
    }).sort(sortByCallerPartOfAndByName);
    return _to_consumable_array(featuredAccessModel).concat(_to_consumable_array(restAccessModel));
};
var ref;
/**
 * Checks if the given access model has view access on the given resource.
 * @param accessModel The access model to check
 * @param resourceId The id of the resource to check for view access
 * @returns True if the access model has view access on the resource, false otherwise
 */ var hasViewAccessOnResource = function(accessModel) {
    var resourceId = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : AccessTableConstants.newResourceTempId;
    return accessModel.accessLevel === AccessLevel.VIEW_ALL || accessModel.accessLevel === AccessLevel.CUSTOM && !((ref = accessModel === null || accessModel === void 0 ? void 0 : accessModel.resourceIdsWithEditLevel) !== null && ref !== void 0 ? ref : []).includes(resourceId);
};
var ref1;
/**
 * Checks if the given access model has edit access on the given resource.
 * @param accessModel The access model to check
 * @param resourceId The id of the resource to check for edit access
 * @returns True if the access model has edit access on the resource, false otherwise
 */ var hasEditAccessOnResource = function(accessModel) {
    var resourceId = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : AccessTableConstants.newResourceTempId;
    return accessModel.accessLevel === AccessLevel.EDIT_ALL || accessModel.accessLevel === AccessLevel.CUSTOM && ((ref1 = accessModel === null || accessModel === void 0 ? void 0 : accessModel.resourceIdsWithEditLevel) !== null && ref1 !== void 0 ? ref1 : []).includes(resourceId);
};
/**
 * Updates the supplied access model to have view access on the given resource.
 * @param accessModel The access model to update
 * @param resourceId The id of the resource to give view access to
 * @returns The updated access model with view access on the resource
 */ var setViewAccessOnResource = function(accessModel) {
    var resourceId = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : AccessTableConstants.newResourceTempId;
    if ([
        AccessLevel.EDIT_ALL,
        AccessLevel.NONE
    ].includes(accessModel.accessLevel)) {
        throw new Error("Cannot set view access on a model with access level ".concat(accessModel.accessLevel));
    }
    if (hasViewAccessOnResource(accessModel, resourceId)) {
        return accessModel;
    }
    if (accessModel.accessLevel === AccessLevel.CUSTOM) {
        var newResourceIdsWithEditLevel = accessModel.resourceIdsWithEditLevel.filter(function(id) {
            return id !== resourceId;
        });
        return _object_spread_props(_object_spread({}, accessModel), {
            resourceIdsWithEditLevel: newResourceIdsWithEditLevel,
            accessLevel: newResourceIdsWithEditLevel.length > 0 ? AccessLevel.CUSTOM : AccessLevel.VIEW_ALL
        });
    }
    throw new Error("Unsupported access level: ".concat(accessModel.accessLevel));
};
/**
 * Updates the supplied access model to have edit access on the given resource.
 * @param accessModel The access model to update
 * @param resourceId The id of the resource to give edit access to
 * @returns The updated access model with edit access on the resource
 */ var setEditAccessOnResource = function(accessModel) {
    var resourceId = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : AccessTableConstants.newResourceTempId;
    if ([
        AccessLevel.EDIT_ALL,
        AccessLevel.NONE
    ].includes(accessModel.accessLevel)) {
        throw new Error("Cannot set edit access on a model with access level ".concat(accessModel.accessLevel));
    }
    if (hasEditAccessOnResource(accessModel, resourceId)) {
        return accessModel;
    }
    if ([
        AccessLevel.VIEW_ALL,
        AccessLevel.CUSTOM
    ].includes(accessModel.accessLevel)) {
        return _object_spread_props(_object_spread({}, accessModel), {
            resourceIdsWithEditLevel: _to_consumable_array(accessModel.resourceIdsWithEditLevel || []).concat([
                resourceId
            ]),
            accessLevel: AccessLevel.CUSTOM
        });
    }
    throw new Error("Unsupported access level: ".concat(accessModel.accessLevel));
};
/**
 * Sorts IdAndDisplayNameModel by id.
 * Mostly used to make sure that the order of the access models is consistent when checking for form dirtiness.
 */ var sortIdNameModels = function(a, b) {
    return a.id.localeCompare(b.id);
};
/**
 * Converts AccessModel[] to IdAndDisplayNameModel[] and sorts them by id.
 * Only includes access models that are modifiable and have edit access on the given resource.
 * @param accessModels The list of access models to serialize
 * @param resourceId The id of the resource to check for edit access
 * @returns Serialized list access that can edit the resource
 */ var serializeAccessModels = function(accessModels) {
    var resourceId = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : AccessTableConstants.newResourceTempId;
    return accessModels.filter(function(accessModel) {
        return isModifiableAccess(accessModel) && hasEditAccessOnResource(accessModel, resourceId);
    }).map(function(param) {
        var id = param.id, displayName = param.displayName;
        return {
            id: id,
            displayName: displayName
        };
    }).sort(sortIdNameModels);
};
/**
 * Overrides the access models with the given value.
 * @param accessModels The base list of access models to override
 * @param value The list of access models that can edit to override with
 * @param resourceId The id of the resource to check for edit access
 * @returns The updated list of access models
 */ var overrideAccessModelsWithValue = function(accessModels, value) {
    var resourceId = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : AccessTableConstants.newResourceTempId;
    return value == null ? accessModels : accessModels.map(function(accessModel) {
        if (isModifiableAccess(accessModel)) {
            var valueOverridesToEdit = value.find(function(access) {
                return access.id === accessModel.id;
            });
            var valueOverridesToView = !valueOverridesToEdit;
            if (valueOverridesToEdit && hasViewAccessOnResource(accessModel, resourceId)) {
                return setEditAccessOnResource(accessModel, resourceId);
            } else if (valueOverridesToView && hasEditAccessOnResource(accessModel, resourceId)) {
                return setViewAccessOnResource(accessModel, resourceId);
            }
        }
        return accessModel;
    });
};
/**
 * Checks whether the given resource is new or not.
 * @param resourceId the id of the resource to check
 * @returns true if the resource is new, false otherwise
 */ var isNewResource = function(resourceId) {
    return !resourceId || resourceId === AccessTableConstants.newResourceTempId;
};
export var AccessTableUtils = {
    sortModels: sortModels,
    isModifiableAccess: isModifiableAccess,
    sortIdNameModels: sortIdNameModels,
    serializeAccessModels: serializeAccessModels,
    hasEditAccessOnResource: hasEditAccessOnResource,
    setEditAccessOnResource: setEditAccessOnResource,
    overrideAccessModelsWithValue: overrideAccessModelsWithValue,
    isNewResource: isNewResource
};

//# sourceMappingURL=AccessTableUtils.js.map